import { useState, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { MapPin, Clock, Cloud, Info, Target, Upload, FileText, X } from 'lucide-react'
import type { SceneSettings } from '@creanizer/types'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../utils/fileReader'

interface SceneChangeModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (newScene: SceneSettings, transition: string) => void
  currentScene: SceneSettings
  uploadedFiles: { id: string; name: string; content: string; size: number }[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<{ id: string; name: string; content: string; size: number }[]>>
}

export function SceneChangeModal({
  open,
  onClose,
  onConfirm,
  currentScene,
  uploadedFiles,
  setUploadedFiles
}: SceneChangeModalProps) {
  const [newScene, setNewScene] = useState<SceneSettings>({
    ...currentScene
  })
  const [transition, setTransition] = useState('')
  const [preset, setPreset] = useState<string>('custom')
  const [keepExistingFiles, setKeepExistingFiles] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // プリセットを適用
  const applyPreset = (presetType: string) => {
    setPreset(presetType)
    switch (presetType) {
      case '1hour':
        setTransition('1時間後')
        break
      case '3hours':
        setTransition('3時間後')
        break
      case 'nextday':
        setTransition('翌日')
        setNewScene(prev => ({ ...prev, timeOfDay: 'morning' }))
        break
      case 'evening':
        setTransition('夕方になって')
        setNewScene(prev => ({ ...prev, timeOfDay: 'evening' }))
        break
      case 'night':
        setTransition('夜になって')
        setNewScene(prev => ({ ...prev, timeOfDay: 'night' }))
        break
      case 'location':
        setTransition('場所を移動して')
        break
      case 'weather':
        setTransition('天気が変わって')
        break
      default:
        setTransition('')
    }
  }

  // ファイルアップロード処理
  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)

      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }

      const newFiles = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: new Blob([info.text]).size
        }))

      setUploadedFiles(prev => [...prev, ...newFiles])
    } catch (error) {
      console.error('File import error:', error)
      alert('ファイルの読み込み中にエラーが発生しました')
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // ファイル削除
  const handleFileRemove = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
  }

  // ファイル内容をシーン遷移説明に挿入
  const handleInsertFilesToTransition = () => {
    const filesContent = uploadedFiles
      .map(file => `【${file.name}】\n${file.content}`)
      .join('\n\n')
    setTransition(prev => prev ? `${prev}\n\n${filesContent}` : filesContent)
  }

  // ファイル内容を背景情報に挿入
  const handleInsertFilesToBackground = () => {
    const filesContent = uploadedFiles
      .map(file => `【${file.name}】\n${file.content}`)
      .join('\n\n')
    setNewScene(prev => ({
      ...prev,
      backgroundInfo: prev.backgroundInfo ? `${prev.backgroundInfo}\n\n${filesContent}` : filesContent
    }))
  }

  const handleConfirm = () => {
    // 既存ファイルをクリアする場合
    if (!keepExistingFiles) {
      setUploadedFiles([])
    }
    onConfirm(newScene, transition)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>シーンを変更して続きを生成</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* プリセット選択 */}
          <div>
            <Label>時間経過プリセット</Label>
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger>
                <SelectValue placeholder="プリセットを選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">カスタム</SelectItem>
                <SelectItem value="1hour">1時間後</SelectItem>
                <SelectItem value="3hours">3時間後</SelectItem>
                <SelectItem value="nextday">翌日</SelectItem>
                <SelectItem value="evening">夕方になって</SelectItem>
                <SelectItem value="night">夜になって</SelectItem>
                <SelectItem value="location">場所を移動</SelectItem>
                <SelectItem value="weather">天気が変化</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* シーン変更の説明 */}
          <div>
            <Label>
              <Clock className="inline w-4 h-4 mr-1" />
              シーン遷移の説明
            </Label>
            <Textarea
              value={transition}
              onChange={(e) => setTransition(e.target.value)}
              placeholder="例：3時間後、別の部屋で / 翌朝、学校の教室で"
              className="h-20"
            />
          </div>

          {/* 場所 */}
          <div>
            <Label>
              <MapPin className="inline w-4 h-4 mr-1" />
              場所
            </Label>
            <Input
              value={newScene.location}
              onChange={(e) => setNewScene(prev => ({ ...prev, location: e.target.value }))}
              placeholder="例：学校の教室、カフェ、自宅のリビング"
            />
          </div>

          {/* 状況 */}
          <div>
            <Label>
              <Info className="inline w-4 h-4 mr-1" />
              状況
            </Label>
            <Textarea
              value={newScene.situation}
              onChange={(e) => setNewScene(prev => ({ ...prev, situation: e.target.value }))}
              placeholder="例：放課後の部活動、ランチタイム、作戦会議中"
              className="h-20"
            />
          </div>

          {/* 雰囲気 */}
          <div>
            <Label>雰囲気</Label>
            <Select
              value={newScene.mood}
              onValueChange={(value: any) => setNewScene(prev => ({ ...prev, mood: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="casual">カジュアル</SelectItem>
                <SelectItem value="serious">シリアス</SelectItem>
                <SelectItem value="romantic">ロマンチック</SelectItem>
                <SelectItem value="tense">緊張感</SelectItem>
                <SelectItem value="comedic">コメディ</SelectItem>
                <SelectItem value="mysterious">ミステリアス</SelectItem>
                <SelectItem value="action">アクション</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 時間帯 */}
          <div>
            <Label>
              <Clock className="inline w-4 h-4 mr-1" />
              時間帯（オプション）
            </Label>
            <Select
              value={newScene.timeOfDay || ''}
              onValueChange={(value) => setNewScene(prev => ({
                ...prev,
                timeOfDay: value as 'morning' | 'afternoon' | 'evening' | 'night' | 'dawn' | undefined
              }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="選択してください" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">指定なし</SelectItem>
                <SelectItem value="dawn">明け方</SelectItem>
                <SelectItem value="morning">朝</SelectItem>
                <SelectItem value="afternoon">午後</SelectItem>
                <SelectItem value="evening">夕方</SelectItem>
                <SelectItem value="night">夜</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 天候 */}
          <div>
            <Label>
              <Cloud className="inline w-4 h-4 mr-1" />
              天候（オプション）
            </Label>
            <Input
              value={newScene.weather || ''}
              onChange={(e) => setNewScene(prev => ({ ...prev, weather: e.target.value || undefined }))}
              placeholder="例：晴れ、雨、雪、曇り"
            />
          </div>

          {/* 目的 */}
          <div>
            <Label>
              <Target className="inline w-4 h-4 mr-1" />
              会話の目的（オプション）
            </Label>
            <Textarea
              value={newScene.objectives?.join('\n') || ''}
              onChange={(e) => setNewScene(prev => ({
                ...prev,
                objectives: e.target.value ? e.target.value.split('\n').filter(o => o.trim()) : undefined
              }))}
              placeholder="例：作戦の相談\n感情の共有\n情報交換（改行で区切る）"
              className="h-20"
            />
          </div>

          {/* ファイルアップロード */}
          <div className="space-y-2 border-t pt-4">
            <Label>
              <FileText className="inline w-4 h-4 mr-1" />
              テキストファイル
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                ファイルを追加
              </Button>
              {uploadedFiles.length > 0 && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleInsertFilesToTransition}
                  >
                    シーン遷移に挿入
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleInsertFilesToBackground}
                  >
                    背景情報に挿入
                  </Button>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT}
              multiple
              onChange={handleFileImport}
              className="hidden"
            />

            {/* アップロード済みファイルのリスト */}
            {uploadedFiles.length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
                <div className="text-xs font-medium text-gray-700 mb-1">読み込み済み：</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between bg-white p-1 rounded border">
                      <div className="flex items-center gap-1">
                        <FileText className="h-3 w-3 text-gray-500" />
                        <span className="text-xs">{file.name}</span>
                        <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleFileRemove(file.id)}
                        className="p-0.5 h-5 w-5"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  合計 {uploadedFiles.length} ファイル
                </div>
              </div>
            )}

            {/* 既存ファイル保持オプション */}
            {uploadedFiles.length > 0 && (
              <div className="flex items-center space-x-2 mt-2">
                <Checkbox
                  id="keepFiles"
                  checked={keepExistingFiles}
                  onCheckedChange={(checked) => setKeepExistingFiles(checked as boolean)}
                />
                <Label htmlFor="keepFiles" className="text-xs cursor-pointer">
                  シーン変更後も既存ファイルを維持する
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button onClick={handleConfirm}>
            シーン変更して続きを生成
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}